import React, { useEffect, useMemo, useState } from 'react';
import { Menu } from 'antd';
import ChevronRight from '../../../assets/images/ChevronRight';
import ArrowBack from '../../../assets/images/ArrowBack';
import {
  CustomizeFilterCalendar,
  CustomShipmentText,
} from '../../../components';
import SearchInput from '@/components/SearchInput';
import { isWorldCourierCompany } from '@/common/constants/worldCourier';
import { useToken } from '@/common/helper';
import { trackSearch } from '@/hooks';
import { baseMenuStyle } from './utils/labels';
import { DIRECT_SHIPMENT_FILTER_KEYS } from './utils/directFilters';
import './Filters.css';

const WC_ONLY_FILTER_KEYS = new Set(['shipper_rtg', 'consignee_rtg']);

export interface FilterOption {
  id: number | string;
  key: string;
  label: string;
}

export interface Permissions {
  locationsView?: boolean;
  transportProvidersView?: boolean;
  productsView?: boolean;
}

export interface SelectedOption {
  id: string | number;
  enabled?: boolean;
  name?: string;
  title?: string;
}

export interface SecondaryFilterOption {
  id: string;
  name?: string;
  title?: string;
  enabled?: boolean;
  disabled?: boolean;
  courier_name?: string;
  label?: string;
  value?: string;
  name2?: string;
  _isPreset?: boolean;
}

export interface FilterGroup {
  group: string;
  items: FilterOption[];
}

interface MainFilterMenuProps {
  showAlertAssignment?: boolean;
  dropdownMaxHeight?: number | string;
  topLevelFilters?: FilterOption[];
  filtersDDL: FilterGroup[];
  permissions?: Permissions;
  handleFilterClick: (filter: FilterOption) => void;
  showSearch?: boolean;
  showGroupTitles?: boolean;
  showDividers?: boolean;
  hiddenFilterKeys?: Set<string>;
  selectedFilterKeys?: Set<string>;
  // Shared with Logiblocks/Data/Home — only the real Shipments page opts in.
  auditSearch?: boolean;
  /**
   * Opt in to Shipper/Consignee RTG for World Courier companies. Off unless a
   * consumer that supports RTG end to end passes it (CT, GxP, AI History,
   * Home KPI); Data and Logiblocks leave it off.
   */
  includeRtgFilters?: boolean;
}

interface ChildPayload {
  dateFilter: string;
  startDate: string;
  endDate: string;
}

interface DateRangeValue {
  dateFilter: string;
  rangeStart: string;
  rangeEnd: string;
}

type Payload = ChildPayload | DateRangeValue;

interface CalendarFilterMenuProps {
  handleDataFromChild: (data: Payload) => void;
  hideBackArrow?: boolean;
  filterTitle: string;
  setSecondFilterDisplay: (display: boolean) => void;
  editingId?: string | null;
  setIsFilterOpen: (open: boolean) => void;
  dateFilter?: string;
}

export const MainFilterMenu: React.FC<MainFilterMenuProps> = ({
  showAlertAssignment = false,
  topLevelFilters = [],
  filtersDDL,
  permissions,
  handleFilterClick,
  showSearch = true,
  showGroupTitles = true,
  showDividers = true,
  dropdownMaxHeight = 550,
  hiddenFilterKeys = new Set(),
  selectedFilterKeys = new Set(),
  auditSearch = false,
  includeRtgFilters,
}) => {
  const [search, setSearch] = useState('');
  const { decodedUserToken } = useToken();
  const isWcCompany = isWorldCourierCompany(
    decodedUserToken()?.enablecustomerleg
  );

  const showRtgFilters = isWcCompany && includeRtgFilters === true;

  const normalizedSearch = search.trim().toLowerCase();

  // Filtered client-side (filter names are already loaded) — track directly,
  // only when opened from the real Shipments page.
  useEffect(() => {
    if (!auditSearch || !normalizedSearch) return;
    trackSearch(normalizedSearch);
  }, [auditSearch, normalizedSearch]);

  const visibleTopLevelFilters = useMemo(
    () =>
      topLevelFilters.filter(
        (item) =>
          !hiddenFilterKeys.has(String(item.key)) &&
          (showRtgFilters || !WC_ONLY_FILTER_KEYS.has(String(item.key)))
      ),
    [topLevelFilters, hiddenFilterKeys, showRtgFilters]
  );

  const visibleGroups = useMemo(
    () =>
      filtersDDL
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              !hiddenFilterKeys.has(String(item.key)) &&
              (item.key !== 'alert_assignment' || showAlertAssignment) &&
              // WC-only RTG dims — shared catalog still lists them for CT/Data.
              (showRtgFilters || !WC_ONLY_FILTER_KEYS.has(String(item.key)))
          ),
        }))
        .filter((group) => group.items.length > 0),
    [filtersDDL, hiddenFilterKeys, showAlertAssignment, showRtgFilters]
  );

  const filteredTopLevelFilters = useMemo(() => {
    if (!normalizedSearch) return visibleTopLevelFilters;

    return visibleTopLevelFilters.filter((item) =>
      item.label.toLowerCase().includes(normalizedSearch)
    );
  }, [visibleTopLevelFilters, normalizedSearch]);

  const filteredGroups = useMemo(() => {
    if (!normalizedSearch) return visibleGroups;

    return visibleGroups
      .map((group) => {
        const groupMatches = group.group
          .toLowerCase()
          .includes(normalizedSearch);

        return {
          ...group,
          items: groupMatches
            ? group.items
            : group.items.filter((item) =>
                item.label.toLowerCase().includes(normalizedSearch)
              ),
        };
      })
      .filter((group) => group.items.length > 0);
  }, [visibleGroups, normalizedSearch]);

  const hasResults =
    filteredTopLevelFilters.length > 0 || filteredGroups.length > 0;

  const isDisabledFilter = (filter: FilterOption) => {
    if (!permissions) return false;

    return (
      (['from_location', 'to_location', 'location_type'].includes(filter.key) &&
        !permissions.locationsView) ||
      (filter.key === 'courier' && !permissions.transportProvidersView) ||
      (['product', 'product_type', 'project_id'].includes(filter.key) &&
        !permissions.productsView)
    );
  };

  const renderDivider = () => <div className='main-filter-menu-divider' />;

  const renderFilterRow = (filter: FilterOption) => {
    const isDisabled = isDisabledFilter(filter);
    const showChevron = !DIRECT_SHIPMENT_FILTER_KEYS.has(filter.key);
    const isSelected = selectedFilterKeys.has(String(filter.key));

    return (
      <Menu.Item
        key={String(filter.id)}
        onClick={() => !isDisabled && handleFilterClick(filter)}
        disabled={isDisabled}
        className='main-filter-menu-item'
      >
        <span
          className={`main-filter-menu-item-label ${isSelected && 'main-filter-menu-item-label-selected'} ${
            isDisabled ? 'main-filter-menu-item-label-disabled' : ''
          }`}
        >
          {filter.label}
        </span>

        {showChevron && (
          <ChevronRight
            className={`main-filter-menu-item-chevron ${
              isDisabled ? 'main-filter-menu-item-chevron-disabled' : ''
            } ${isSelected ? 'selected-main-filter' : ''}`}
            fill={isDisabled ? '#B0B0B0' : '#5E5E5E'}
          />
        )}
      </Menu.Item>
    );
  };

  return (
    <Menu
      style={
        {
          maxHeight: dropdownMaxHeight,
          overflowY: 'auto',
          padding: 0,
        } as React.CSSProperties
      }
      selectable={false}
      className='main-filter-menu-wrapper'
    >
      <div className='main-filter-menu-header'>
        <CustomShipmentText
          text='Filter by'
          fontWeight='600'
          color='var(--text-secondary)'
          marginTop={'10px'}
          marginBottom={showSearch ? '10px' : '0px'}
        />
        {showSearch && (
          <SearchInput
            placeholder='Search filters'
            defaultValue={search}
            onChange={setSearch}
            onClear={() => setSearch('')}
            style={{ width: '100%' }}
          />
        )}
      </div>

      {hasResults ? (
        <>
          {filteredTopLevelFilters.map((filter, index) => {
            const shouldShowDividerAfterTopLevel =
              index === filteredTopLevelFilters.length - 1 &&
              filteredGroups.length > 0;

            return (
              <React.Fragment key={String(filter.id)}>
                {renderFilterRow(filter)}
                {showDividers &&
                  shouldShowDividerAfterTopLevel &&
                  renderDivider()}
              </React.Fragment>
            );
          })}

          {filteredGroups.map((group, groupIndex) => (
            <React.Fragment key={group.group}>
              {showGroupTitles && (
                <div className='main-filter-menu-group-title'>
                  {group.group}
                </div>
              )}

              {group.items.map((filter) => renderFilterRow(filter))}

              {showDividers &&
                groupIndex !== filteredGroups.length - 1 &&
                renderDivider()}
            </React.Fragment>
          ))}
        </>
      ) : (
        <div className='main-filter-menu-empty'>No matching filters</div>
      )}
    </Menu>
  );
};

export const CalendarFilterMenu: React.FC<CalendarFilterMenuProps> = ({
  handleDataFromChild,
  hideBackArrow,
  filterTitle,
  setSecondFilterDisplay,
  editingId,
  setIsFilterOpen,
  dateFilter,
}) => {
  const computedDateFilter = (() => {
    if (dateFilter) return dateFilter;

    const title = String(filterTitle).toLowerCase();

    if (title.includes('pickup')) return 'scheduled_pickup_date';
    if (title.includes('delivery')) return 'scheduled_delivery_date';
    if (title.includes('customer')) return 'customer_defined_ship_date';
    if (title.includes('created')) return 'created_at';

    return 'scheduled_pickup_date';
  })();

  return (
    <Menu
      style={
        {
          ...baseMenuStyle,
          width: 'auto',
        } as React.CSSProperties
      }
      selectable={false}
      className='main-filter-menu-wrapper'
    >
      <Menu.ItemGroup
        title={
          <div className='calendar-filter-menu-header'>
            {!hideBackArrow && (
              <ArrowBack
                fill='var(--text-secondary)'
                style={{ width: 16, height: 16, cursor: 'pointer' }}
                onClick={() => {
                  setSecondFilterDisplay(false);
                  setIsFilterOpen(true);
                }}
              />
            )}
            <CustomShipmentText
              text={filterTitle}
              fontWeight='600'
              color='var(--text-secondary)'
            />
          </div>
        }
      >
        <CustomizeFilterCalendar
          editingId={(!hideBackArrow ? null : editingId) ?? undefined}
          dateFilter={computedDateFilter}
          handleDataFromChild={handleDataFromChild}
        />
      </Menu.ItemGroup>
    </Menu>
  );
};
